import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WorkOrderList.css';
import FragranceGroupView from './FragranceGroupView';
import BatchManager from './BatchManager';
import BatchSchedule from './BatchSchedule';
import SchedulingDashboard from './SchedulingDashboard';

function WorkOrderList() {
  const [activeTab, setActiveTab] = useState('orders');
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    return localStorage.getItem('autoRefresh') === 'true';
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [limit, setLimit] = useState(50); // Start with 50 items
  const [filterFragrance, setFilterFragrance] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterWorkOrder, setFilterWorkOrder] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [availableFragrances, setAvailableFragrances] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([])
  const [showFragranceSuggestions, setShowFragranceSuggestions] = useState(false);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [yopPasswordPrompt, setYopPasswordPrompt] = useState(null);
  const [yopPassword, setYopPassword] = useState('');

  const navigate = useNavigate();
  const pollInterval = useRef(null);
  const [sortColumn, setSortColumn] = useState(null); // null means use server-side sorting (priority first)
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch work orders
  const fetchWorkOrders = async (showLoading = false, forceRefresh = false) => {
    // Skip if data already loaded and not forcing refresh
    if (isDataLoaded && !forceRefresh && !showLoading) {
      return;
    }

    if (showLoading) setLoading(true);

    try {
      const params = {
        limit: limit,
        status: 'all' // Always show all active statuses
      };
      if (filterFragrance.trim()) {
        params.fragrance = filterFragrance.trim();
      }
      if (filterCustomer.trim()) {
        params.customer = filterCustomer.trim();
      }
      if (filterWorkOrder.trim()) {
        params.workOrderNumber = filterWorkOrder.trim();
      }
      if (dateFrom) {
        params.dateFrom = dateFrom;
      }
      if (dateTo) {
        params.dateTo = dateTo;
      }

      const response = await axios.get('/api/workorders', { params });

      // Extract unique fragrances and customers
      const fragrances = [...new Set(response.data
        .map(wo => wo.fragranceName || wo.fragranceOilName || '')
        .filter(f => f && f !== ' - '))];
      const customers = [...new Set(response.data
        .map(wo => wo.customerName)
        .filter(c => c))];

      setAvailableFragrances(fragrances.sort());
      setAvailableCustomers(customers.sort());

      // Set work orders (sorting handled by sortedWorkOrders computed value)
      setWorkOrders(response.data);
      setLastUpdate(new Date());
      setError(null);
      setIsDataLoaded(true);
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
      setError('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };


  // Listen for localStorage changes from Settings page
  useEffect(() => {
    const handleStorageChange = () => {
      setAutoRefresh(localStorage.getItem('autoRefresh') === 'true');
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Setup polling for real-time updates
  useEffect(() => {
    // Only fetch if on the work orders tab
    if (activeTab === 'orders') {
      // Initial fetch only if not already loaded
      if (!isDataLoaded) {
        fetchWorkOrders(true);
      }
    }

    // Setup polling if auto-refresh is enabled
    if (autoRefresh && activeTab === 'orders') {
      pollInterval.current = setInterval(() => {
        fetchWorkOrders(false, true); // Force refresh on polls
      }, 30000); // Poll every 30 seconds instead of 5
    }

    // Cleanup
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [autoRefresh, activeTab, limit]);

  // Debounced search effect for filters
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (isDataLoaded) {
        fetchWorkOrders(true, true); // Force refresh with new filters
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filterFragrance, filterCustomer, filterWorkOrder, dateFrom, dateTo]);

  // Handle manual refresh
  const handleManualRefresh = async () => {
    setLoading(true);
    try {
      // Reset to initial load size on manual refresh
      setLimit(50);
      setIsDataLoaded(false);

      // Only refresh cache if we're using cached data
      if (process.env.REACT_APP_USE_CACHE !== 'false') {
        await axios.post('/api/workorders/refresh');
      }
      await fetchWorkOrders(true, true); // Force refresh with loading
    } catch (err) {
      console.error('Failed to refresh:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load more work orders
  const handleLoadMore = async () => {
    const newLimit = limit + 50;
    setLimit(newLimit);
    setIsDataLoaded(false); // Force refresh with new limit
    await fetchWorkOrders(true, true);
  };

  // Handle status change
  const handleStatusChange = async (workOrderId, newStatus) => {
    try {
      await axios.patch(`/api/workorders/${workOrderId}/status`, {
        status: newStatus
      });
      // Refresh list after status change
      await fetchWorkOrders(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update work order status');
    }
  };

  // Handle priority change
  const handlePriorityChange = async (workOrder, newPriority) => {
    // If TOP selected, prompt for password
    if (newPriority === 'TOP') {
      setYopPasswordPrompt({ workOrder, priority: newPriority });
      return;
    }

    // Update priority directly for non-TOP priorities
    try {
      await axios.patch(`/api/workorders/${workOrder.id}/priority`, {
        priority: newPriority
      });
      // Update local state
      setWorkOrders(workOrders.map(wo =>
        wo.id === workOrder.id ? { ...wo, priority: newPriority } : wo
      ));
    } catch (err) {
      console.error('Failed to update priority:', err);
      alert('Failed to update work order priority');
    }
  };

  // Handle TOP password submit
  const handleYopPasswordSubmit = async () => {
    if (!yopPasswordPrompt) return;

    try {
      await axios.patch(`/api/workorders/${yopPasswordPrompt.workOrder.id}/priority`, {
        priority: 'TOP',
        password: yopPassword
      });
      // Update local state
      setWorkOrders(workOrders.map(wo =>
        wo.id === yopPasswordPrompt.workOrder.id ? { ...wo, priority: 'TOP' } : wo
      ));
      // Close modal
      setYopPasswordPrompt(null);
      setYopPassword('');
    } catch (err) {
      console.error('Failed to update priority:', err);
      if (err.response?.status === 403) {
        alert('Invalid password for TOP priority');
      } else {
        alert('Failed to update work order priority');
      }
    }
  };

  // Navigate to work order details
  const handleWorkOrderClick = (workOrderNumber) => {
    navigate(`/workorders/${workOrderNumber}`);
  };

  // Open work order in NetSuite
  const openInNetSuite = (e, workOrderId) => {
    e.stopPropagation();
    const netsuiteUrl = `https://3646798.app.netsuite.com/app/accounting/transactions/workord.nl?id=${workOrderId}`;
    window.open(netsuiteUrl, '_blank');
  };

  // No longer need client-side filtering - it's done server-side now
  const getFilteredWorkOrders = () => {
    return sortedWorkOrders;
  };

  // Get filtered suggestions for autocomplete
  const getFilteredFragrances = () => {
    if (!filterFragrance.trim()) return availableFragrances;
    return availableFragrances.filter(f =>
      f.toLowerCase().includes(filterFragrance.toLowerCase())
    );
  };

  const getFilteredCustomers = () => {
    if (!filterCustomer.trim()) return availableCustomers;
    return availableCustomers.filter(c =>
      c.toLowerCase().includes(filterCustomer.toLowerCase())
    );
  };

  // Handle column sort
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort data based on column (server already sorted by priority)
  const sortedWorkOrders = !sortColumn ? workOrders : [...workOrders].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Handle dates
    if (sortColumn.includes('Date') || sortColumn === 'dateScheduled') {
      aVal = new Date(aVal || 0);
      bVal = new Date(bVal || 0);
    }

    // Handle numbers
    if (typeof aVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    // Handle strings
    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return 0;
  });

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'Planning': '#9370db',      // Medium purple
      'Pending Build': '#ffa500', // Orange
      'In Process': '#4169e1',    // Royal blue
      'Built': '#32cd32',         // Lime green
      'Cancelled': '#dc143c',     // Crimson
      'Closed': '#808080'         // Gray
    };
    return statusColors[status] || '#666';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && workOrders.length === 0) {
    return (
      <div className="loading-container">
        <div className="loader">Loading work orders...</div>
      </div>
    );
  }

  return (
    <div className="work-order-list">
      {/* Tab Navigation */}
      <div className="tabs-navigation">
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Work Orders
        </button>
        <button
          className={`tab-button ${activeTab === 'scheduling' ? 'active' : ''}`}
          onClick={() => setActiveTab('scheduling')}
        >
          Auto Schedule
        </button>
        <button
          className={`tab-button ${activeTab === 'blending' ? 'active' : ''}`}
          onClick={() => setActiveTab('blending')}
        >
          Batching Worksheet
        </button>
        <button
          className={`tab-button ${activeTab === 'batches' ? 'active' : ''}`}
          onClick={() => setActiveTab('batches')}
        >
          Batch List
        </button>
        <button
          className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedule')}
        >
          Batch Schedule
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'scheduling' ? (
        <SchedulingDashboard />
      ) : activeTab === 'blending' ? (
        <FragranceGroupView />
      ) : activeTab === 'batches' ? (
        <BatchManager />
      ) : activeTab === 'schedule' ? (
        <BatchSchedule />
      ) : (
        <>
          {/* Original Work Orders Content */}

      {/* Filters */}
      <div className="list-controls">
        <div className="filter-controls">
          <div className="filter-input-group">
            <label>Scheduled Date:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
              placeholder="From"
            />
            <span style={{ margin: '0 5px' }}>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
              placeholder="To"
            />
            {(dateFrom || dateTo) && (
              <button
                className="clear-filter-btn"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
                type="button"
              >
                ×
              </button>
            )}
          </div>

          <div className="filter-input-group">
            <label>Fragrance:</label>
            <div className="autocomplete-wrapper">
              <input
                type="text"
                value={filterFragrance}
                onChange={(e) => setFilterFragrance(e.target.value)}
                onFocus={() => setShowFragranceSuggestions(true)}
                onBlur={() => setTimeout(() => setShowFragranceSuggestions(false), 200)}
                placeholder="Type to filter..."
                className="filter-input"
              />
              {filterFragrance && (
                <button
                  className="clear-filter-btn"
                  onClick={() => setFilterFragrance('')}
                  type="button"
                >
                  ×
                </button>
              )}
              {showFragranceSuggestions && filterFragrance && getFilteredFragrances().length > 0 && (
                <div className="suggestions-dropdown">
                  {getFilteredFragrances().slice(0, 10).map(fragrance => (
                    <div
                      key={fragrance}
                      className="suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setFilterFragrance(fragrance);
                        setShowFragranceSuggestions(false);
                      }}
                    >
                      {fragrance}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-input-group">
            <label>WO Number:</label>
            <input
              type="text"
              value={filterWorkOrder}
              onChange={(e) => setFilterWorkOrder(e.target.value)}
              placeholder="Search WO number..."
              className="filter-input"
              style={{ minWidth: '150px' }}
            />
            {filterWorkOrder && (
              <button
                className="clear-filter-btn"
                onClick={() => setFilterWorkOrder('')}
                type="button"
              >
                ×
              </button>
            )}
          </div>

          <div className="filter-input-group">
            <label>Customer:</label>
            <div className="autocomplete-wrapper">
              <input
                type="text"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                onFocus={() => setShowCustomerSuggestions(true)}
                onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                placeholder="Type to filter..."
                className="filter-input"
              />
              {filterCustomer && (
                <button
                  className="clear-filter-btn"
                  onClick={() => setFilterCustomer('')}
                  type="button"
                >
                  ×
                </button>
              )}
              {showCustomerSuggestions && filterCustomer && getFilteredCustomers().length > 0 && (
                <div className="suggestions-dropdown">
                  {getFilteredCustomers().slice(0, 10).map(customer => (
                    <div
                      key={customer}
                      className="suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setFilterCustomer(customer);
                        setShowCustomerSuggestions(false);
                      }}
                    >
                      {customer}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Work Orders Table */}
      <div className="table-container">
        <table className="work-orders-table">
          <thead>
            <tr>
              <th onClick={() => handleColumnSort('priority')} style={{cursor: 'pointer'}}>
                Priority {sortColumn === 'priority' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('workOrderNumber')} style={{cursor: 'pointer'}}>
                WO Number {sortColumn === 'workOrderNumber' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('customerName')} style={{cursor: 'pointer'}}>
                Customer {sortColumn === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('itemDescription')} style={{cursor: 'pointer'}}>
                Item {sortColumn === 'itemDescription' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('quantity')} style={{cursor: 'pointer'}}>
                Qty Ordered {sortColumn === 'quantity' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('quantityCompleted')} style={{cursor: 'pointer'}}>
                Qty Built {sortColumn === 'quantityCompleted' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('dateRequired')} style={{cursor: 'pointer'}}>
                Date Needed {sortColumn === 'dateRequired' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedWorkOrders.map((wo) => (
              <tr
                key={wo.id}
                onClick={() => handleWorkOrderClick(wo.workOrderNumber)}
                className="clickable-row"
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <select
                    value={wo.priority || 'Normal'}
                    onChange={(e) => handlePriorityChange(wo, e.target.value)}
                    className={`priority-select priority-${(wo.priority || 'normal').toLowerCase()}`}
                  >
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                    <option value="TOP">📌 TOP</option>
                  </select>
                </td>
                <td className="wo-number">
                  <a
                    href="#"
                    onClick={(e) => openInNetSuite(e, wo.id)}
                    style={{color: '#2c5282', textDecoration: 'none'}}
                    title="Open in NetSuite"
                  >
                    {wo.workOrderNumber}
                  </a>
                </td>
                <td>{wo.customerName || ''}</td>
                <td>
                  <div className="item-info">
                    <div className="item-sku">{wo.itemSku}</div>
                    <div className="item-desc">{wo.itemDescription}</div>
                    {wo.memo && <div className="item-memo">{wo.memo}</div>}
                  </div>
                </td>
                <td className="quantity">
                  {wo.quantity || 0}
                </td>
                <td className="quantity">
                  {wo.quantityCompleted || 0}
                </td>
                <td>{formatDate(wo.dateRequired || wo.dateScheduled || wo.scheduledEndDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {getFilteredWorkOrders().length === 0 && !loading && (
          <div className="no-data">
            No work orders found matching the selected filters
          </div>
        )}

        {/* Load More Button */}
        {workOrders.length >= limit && getFilteredWorkOrders().length > 0 && (
          <div className="load-more-container" style={{
            textAlign: 'center',
            padding: '20px',
            marginTop: '10px'
          }}>
            <button
              className="load-more-btn"
              onClick={handleLoadMore}
              disabled={loading}
              style={{
                padding: '10px 30px',
                fontSize: '14px',
                backgroundColor: loading ? '#808080' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Loading more...' : 'Load More Work Orders'}
            </button>
          </div>
        )}
      </div>

      {/* Live Update Indicator */}
      {autoRefresh && (
        <div className="live-indicator">
          <span className="pulse"></span>
          LIVE
        </div>
      )}

      {/* TOP Password Modal */}
      {yopPasswordPrompt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>📌 TOP Priority Password Required</h3>
            <p>Enter password to set TOP priority for WO {yopPasswordPrompt.workOrder.workOrderNumber}:</p>
            <input
              type="password"
              value={yopPassword}
              onChange={(e) => setYopPassword(e.target.value)}
              placeholder="Enter password"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleYopPasswordSubmit();
                }
              }}
            />
            <div className="modal-actions">
              <button onClick={handleYopPasswordSubmit} className="submit-btn">
                Submit
              </button>
              <button onClick={() => {
                setYopPasswordPrompt(null);
                setYopPassword('');
              }} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default WorkOrderList;